import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { fetchUserAttributes } from 'aws-amplify/auth';
import { PenTool, BookOpen, Users, MapPin, Zap, ChevronDown, ChevronUp } from 'lucide-react';
import { apiGet } from '../api';
import DesktopAppNote from '../components/DesktopAppNote';
import '../App.css';

export interface Book {
  entityId: string;
  title: string;
  updatedAt: string;
  coverUrl?: string;
  currentChapter?: { number: number; title?: string };
  wordCount?: number;
  sessionWords?: number;
  coverGradient?: 'a' | 'b' | 'c';
  initials?: string;
}

interface WorldCounts {
  characters: number;
  locations: number;
  events: number;
}

interface RecentEntity {
  entityId: string;
  name: string;
  type: 'character' | 'location' | 'event';
  updatedAt?: string;
}

const TRANSPARENT_PIXEL =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

const HOW_TO_GUIDES = [
  {
    title: 'Upload a Book',
    description: 'Move your work to the cloud to automatic backups and revision management',
    src: '/how-to/upload-book.svg',
    slug: 'cloud-sync',
  },
  {
    title: 'Import into Chaptery',
    description: 'Already started on your work in another tool? Importing it into Chaptery is simple',
    src: '/how-to/import-book.svg',
    slug: 'import-word',
  },
  {
    title: 'Add Characters & Relationships',
    description: 'Keep track of your worlds. Powerful tools to keep track of your characters, locations and more',
    src: '/how-to/add-characters.svg',
    slug: 'build-worlds',
  },
  {
    title: 'Export to PDF',
    description: 'Ready to send out a hard copy? Export to PDF is a snap',
    src: '/how-to/export-pdf.svg',
    slug: 'export-pdf',
  },
  {
    title: 'Create Scenes',
    description: 'Creativity can be messy, organize your thoughts with scenes',
    src: '/how-to/create-scenes.svg',
    slug: 'chapters-scenes',
  },
];

const COVER_GRADIENTS = ['a', 'b', 'c'] as const;

function bookInitials(title: string): string {
  const words = title.trim().split(/\s+/);
  if (words.length > 1) {
    return words.map(w => w[0]).join('').toUpperCase().slice(0, 3);
  }
  // CamelCase single word — extract capital letters
  const caps = title.replace(/[^A-Z]/g, '');
  if (caps.length >= 2) return caps.slice(0, 3);
  return title.slice(0, 3).toUpperCase();
}

function timeAwareGreeting(name: string): string {
  const h = new Date().getHours();
  if (h < 5)  return `Up late, ${name}.`;
  if (h < 12) return `Good morning, ${name}.`;
  if (h < 17) return `Good afternoon, ${name}.`;
  if (h < 21) return `Good evening, ${name}.`;
  return `Up late, ${name}.`;
}

function relativeTime(isoString: string): string {
  if (!isoString) return '—';
  const d = new Date(isoString);
  if (isNaN(d.getTime())) return '—';
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

const HowToStrip: React.FC = () => {
  const [expanded, setExpanded] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem('chaptery.home.howto.expanded') === 'true';
  });
  useEffect(() => {
    window.localStorage.setItem('chaptery.home.howto.expanded', String(expanded));
  }, [expanded]);

  return (
    <div className="v2-howto-strip">
      <button className="v2-howto-toggle" onClick={() => setExpanded(e => !e)}>
        <BookOpen size={16} />
        <span>How-to guides</span>
        <span className="v2-howto-count">{HOW_TO_GUIDES.length}</span>
        {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>
      {expanded && (
        <div className="howto-grid v2-howto-grid">
          {HOW_TO_GUIDES.map(guide => (
            <Link key={guide.title} to={`/help/article/${guide.slug}`} className="howto-card" style={{ textDecoration: 'none', color: 'inherit' }}>
              <div className="howto-gif">
                <img
                  src={guide.src}
                  alt={guide.title}
                  onError={e => { (e.target as HTMLImageElement).src = TRANSPARENT_PIXEL; }}
                />
              </div>
              <strong>{guide.title}</strong>
              <p>{guide.description}</p>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

// accountId (the ID token's sub) tells the empty bookshelf card whether this account was
// emailed its download link (#938). Required, so App.tsx can't silently drop it.
// isSubscriber comes from App; unset means a plan, as for tests (the server is the gate).
const HomePage = ({ accountId, isSubscriber = true }: { accountId: string | null; isSubscriber?: boolean }) => {
  const navigate = useNavigate();
  const [greeting, setGreeting] = useState('');
  const [books, setBooks] = useState<Book[]>([]);
  const [worldCounts, setWorldCounts] = useState<WorldCounts>({ characters: 0, locations: 0, events: 0 });
  const [recentEntities, setRecentEntities] = useState<RecentEntity[]>([]);
  const [loading, setLoading] = useState(true);
  const [appNotFound, setAppNotFound] = useState(false);
  // The books could not be loaded: say so rather than show the new-author panel.
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    const nameFrom = (attrs: object) => {
      const { given_name, name: cognitoName, email } = attrs as { given_name?: string; name?: string; email?: string };
      return given_name ?? cognitoName ?? email?.split('@')[0] ?? 'there';
    };
    // A load from before the plan changed must not land after it.
    let cancelled = false;
    setLoading(true);
    setLoadFailed(false);
    const load = async () => {
      try {
        // Without a plan the server refuses the books and the world (#1059): ask only for
        // the name, and say why there is nothing else here.
        if (!isSubscriber) {
          const attrs = await fetchUserAttributes().catch(() => ({}));
          if (!cancelled) setGreeting(nameFrom(attrs));
          return;
        }
        const [booksRes, worldRes, attrs] = await Promise.all([
          apiGet('/books'),
          apiGet('/entities'),
          fetchUserAttributes().catch(() => ({})),
        ]);
        if (cancelled) return;
        setGreeting(nameFrom(attrs));

        const booksData = (await booksRes.body.json()) as unknown as (Book & {
          currentRevision?: string;
          revisions?: Record<string, { wordCount?: number; updatedAt?: string }>;
        })[];
        const sorted = Array.isArray(booksData)
          ? [...booksData]
              .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
              .map((b, i) => ({
                ...b,
                wordCount: b.wordCount ?? (b.currentRevision ? b.revisions?.[b.currentRevision]?.wordCount : undefined),
                coverGradient: b.coverGradient ?? COVER_GRADIENTS[i % 3],
                initials: b.initials ?? bookInitials(b.title),
              }))
          : [];
        setBooks(sorted);

        const worldData = (await worldRes.body.json()) as {
          characters?: Record<string, unknown>[];
          locations?: Record<string, unknown>[];
          events?: Record<string, unknown>[];
        };
        setWorldCounts({
          characters: (worldData.characters ?? []).length,
          locations: (worldData.locations ?? []).length,
          events: (worldData.events ?? []).length,
        });

        const allEntities: RecentEntity[] = [
          ...(worldData.characters ?? []).map(e => ({ ...e, type: 'character' as const })),
          ...(worldData.locations ?? []).map(e => ({ ...e, type: 'location' as const })),
          ...(worldData.events ?? []).map(e => ({ ...e, type: 'event' as const })),
        ] as RecentEntity[];
        setRecentEntities(
          allEntities
            .sort((a, b) => new Date(b.updatedAt ?? 0).getTime() - new Date(a.updatedAt ?? 0).getTime())
            .slice(0, 3)
        );
      } catch (err) {
        console.error('HomePage: failed to load dashboard data', err);
        if (!cancelled) setLoadFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [isSubscriber]);

  if (loading) {
    return <div className="home-page"><p>Loading...</p></div>;
  }

  // Not an empty home, which read as if the books were gone: they are refused, not lost.
  // Wording approved by the owner (#1059).
  if (!isSubscriber) {
    return (
      <div className="home-page home-page-v2">
        <div className="v2-greeting-block">
          <h1 className="home-greeting v2-greeting">{timeAwareGreeting(greeting)}</h1>
        </div>
        <div className="v2-hero-card v2-hero-card--empty home-plan-needed">
          <div>
            <h2 className="v2-hero-title">Your books and world are safe.</h2>
            <p className="v2-greeting-sub">Choose a plan to open them again.</p>
            <Link to="/pricing" className="btn-primary-large">See plans</Link>
          </div>
        </div>
      </div>
    );
  }

  const recentBook = books[0] ?? null;
  const totalWords = books.reduce((s, b) => s + (b.wordCount ?? 0), 0);
  const worldTotal = worldCounts.characters + worldCounts.locations + worldCounts.events;

  return (
    <div className="home-page home-page-v2">

      {/* Greeting */}
      <div className="v2-greeting-block">
        <h1 className="home-greeting v2-greeting">{timeAwareGreeting(greeting)}</h1>
        {recentBook?.sessionWords ? (
          <p className="v2-greeting-sub">
            You wrote <strong>{recentBook.sessionWords.toLocaleString()} words</strong>
            {' '}in <em>{recentBook.title}</em> yesterday. Pick up where you left off.
          </p>
        ) : recentBook ? (
          <p className="v2-greeting-sub">
            Pick up where you left off in <em>{recentBook.title}</em>.
          </p>
        ) : null /* No books: the start hero below says what to do (#1130). */}
      </div>

      {/* HERO — Continue Writing (full width) */}
      {recentBook ? (
        <div className="v2-hero-card">
          <div className="v2-hero-left">
            <div className="home-card-label">CONTINUE WRITING</div>
            <h2 className="v2-hero-title">{recentBook.title}</h2>
            {recentBook.currentChapter && (
              <p className="v2-hero-chapter">
                Chapter {recentBook.currentChapter.number}
                {recentBook.currentChapter.title && <> · &ldquo;{recentBook.currentChapter.title}&rdquo;</>}
              </p>
            )}
            <div className="v2-hero-meta">
              {recentBook.wordCount != null && (
                <>
                  <div className="v2-stat">
                    <span className="v2-stat-num">{recentBook.wordCount.toLocaleString()}</span>
                    <span className="v2-stat-label">words</span>
                  </div>
                  <div className="v2-stat-divider" />
                </>
              )}
              {recentBook.sessionWords != null && (
                <>
                  <div className="v2-stat">
                    <span className="v2-stat-num">+{recentBook.sessionWords.toLocaleString()}</span>
                    <span className="v2-stat-label">yesterday</span>
                  </div>
                  <div className="v2-stat-divider" />
                </>
              )}
              <div className="v2-stat">
                <span className="v2-stat-num">{relativeTime(recentBook.updatedAt)}</span>
                <span className="v2-stat-label">last edited</span>
              </div>
            </div>
            <button
              className="btn-primary-large"
              onClick={() => {
                setAppNotFound(false);
                window.location.href = `chaptery://open?bookId=${recentBook.entityId.replace(/^BOOK#/, '')}`;
                const timer = setTimeout(() => setAppNotFound(true), 4000);
                const onHide = () => { clearTimeout(timer); document.removeEventListener('visibilitychange', onHide); };
                document.addEventListener('visibilitychange', onHide);
              }}
            >
              <PenTool size={18} strokeWidth={2} />
              <span>Open in Chaptery</span>
            </button>
            {appNotFound && (
              <p className="home-card-app-missing">
                Chaptery isn't open.{' '}
                <button className="btn-link" onClick={() => navigate('/downloads')}>Download it</button>
                {' '}or open it on your machine and try again.
              </p>
            )}
          </div>
          <div className="v2-hero-right">
            {recentBook.coverUrl ? (
              <img
                src={recentBook.coverUrl}
                alt={recentBook.title}
                className="v2-cover v2-cover--img"
              />
            ) : (
              <div className={`v2-cover v2-cover--${recentBook.coverGradient ?? 'a'}`} aria-label={recentBook.title}>
                {recentBook.initials ?? bookInitials(recentBook.title)}
              </div>
            )}
          </div>
        </div>
      ) : loadFailed ? (
        <div className="v2-hero-card v2-hero-card--empty">
          <div className="dl-download-error" role="alert">We couldn't load your bookshelf. Refresh the page to try again.</div>
        </div>
      ) : (
        // A fresh account (#1130): the start panel is the hero, with the app beside it.
        <DesktopAppNote accountId={accountId} variant="hero" headingLevel={2} />
      )}

      {/* Secondary row — My Books + World Builder */}
      <div className="v2-secondary-row">
        <div className="home-card v2-secondary-card">
          <div className="v2-card-header">
            <div className="home-card-label">MY BOOKS</div>
            <button className="v2-card-link" onClick={() => navigate('/books')}>View all →</button>
          </div>
          {books.length > 0 ? (
            <>
              <div className="v2-book-spines-real">
                {books.slice(0, 4).map(b => (
                  <div key={b.entityId} className="v2-book-spine-real" title={b.title}>
                    {b.coverUrl ? (
                      <img src={b.coverUrl} alt={b.title} className="v2-book-spine-cover v2-book-spine-cover--img" />
                    ) : (
                      <div className={`v2-book-spine-cover v2-cover--${b.coverGradient ?? 'a'}`}>
                        {b.initials ?? bookInitials(b.title)}
                      </div>
                    )}
                    <span className="v2-book-spine-label">{b.title}</span>
                  </div>
                ))}
              </div>
              <div className="v2-card-stat-row">
                <span className="v2-card-stat-num">{books.length}</span> book{books.length !== 1 ? 's' : ''}
                {totalWords > 0 && (
                  <> · <span className="v2-card-stat-num">{totalWords.toLocaleString()}</span> total words</>
                )}
              </div>
            </>
          ) : (
            <div className="home-card-empty">
              {/* Not after a failed load: the hero above already says the shelf didn't load. */}
              {!loadFailed && <p>No books yet.</p>}
              <button className="btn-secondary small-btn" onClick={() => navigate('/books')}>Go to Library →</button>
            </div>
          )}
        </div>

        <div className="home-card v2-secondary-card">
          <div className="v2-card-header">
            <div className="home-card-label">WORLD BUILDER</div>
            <button className="v2-card-link" onClick={() => navigate('/world')}>Open World →</button>
          </div>
          {worldTotal > 0 ? (
            <>
              <div className="v2-world-chips">
                {recentEntities.map(e => (
                  <span key={e.entityId} className={`v2-world-chip v2-world-chip--${e.type}`}>
                    {e.type === 'character' && <Users size={12} />}
                    {e.type === 'location' && <MapPin size={12} />}
                    {e.type === 'event' && <Zap size={12} />}
                    <span>{e.name}</span>
                  </span>
                ))}
                {worldTotal > 3 && (
                  <span className="v2-world-chip v2-world-chip--more">+{worldTotal - 3} more</span>
                )}
              </div>
              <div className="v2-card-stat-row">
                <span className="v2-card-stat-num">{worldCounts.characters}</span> characters ·{' '}
                <span className="v2-card-stat-num">{worldCounts.locations}</span> locations ·{' '}
                <span className="v2-card-stat-num">{worldCounts.events}</span> events
              </div>
            </>
          ) : (
            <div className="home-card-empty">
              <p>No characters yet — start building your world</p>
              <button className="btn-secondary small-btn" onClick={() => navigate('/world')}>Start Building →</button>
            </div>
          )}
        </div>
      </div>

      <HowToStrip />
    </div>
  );
};

export default HomePage;
